import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Book as BookIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { motion } from 'framer-motion';

const SubjectManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [stats, setStats] = useState({
    totalSubjects: 0,
    coreSubjects: 0,
    electiveSubjects: 0,
    activeSubjects: 0
  });

  const [formData, setFormData] = useState({
    subject_name: '',
    subject_code: '',
    description: '',
    type: 'core',
    credits: '',
    teacher_id: '',
    class_level: '',
    is_active: true
  });

  const subjectTypes = [
    { value: 'core', label: 'Core Subject' },
    { value: 'elective', label: 'Elective Subject' },
    { value: 'extra', label: 'Extra Curricular' }
  ];

  const classLevels = ['Nursery', 'LKG', 'UKG', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  const cdcSubjects = {
    'Nursery': ['Nepali', 'English', 'Mathematics', 'My Surroundings', 'Creative Arts'],
    'LKG': ['Nepali', 'English', 'Mathematics', 'My Surroundings', 'Creative Arts'],
    'UKG': ['Nepali', 'English', 'Mathematics', 'My Surroundings', 'Creative Arts'],
    '1': ['Nepali', 'English', 'Mathematics', 'Mero Serophero (My Surroundings)', 'Creative Arts'],
    '2': ['Nepali', 'English', 'Mathematics', 'Mero Serophero (My Surroundings)', 'Creative Arts'],
    '3': ['Nepali', 'English', 'Mathematics', 'Mero Serophero (My Surroundings)', 'Creative Arts'],
    '4': ['Nepali', 'English', 'Mathematics', 'Social Studies', 'Science & Environment', 'Health & Physical Education', 'Creative Arts', 'Moral Education'],
    '5': ['Nepali', 'English', 'Mathematics', 'Social Studies', 'Science & Environment', 'Health & Physical Education', 'Creative Arts', 'Moral Education'],
    '6': ['Nepali', 'English', 'Mathematics', 'Science & Technology', 'Social Studies', 'Health & Physical Education', 'Occupation, Business & Technology', 'Moral Education', 'Local Subject'],
    '7': ['Nepali', 'English', 'Mathematics', 'Science & Technology', 'Social Studies', 'Health & Physical Education', 'Occupation, Business & Technology', 'Moral Education', 'Local Subject'],
    '8': ['Nepali', 'English', 'Mathematics', 'Science & Technology', 'Social Studies', 'Health & Physical Education', 'Occupation, Business & Technology', 'Moral Education', 'Local Subject'],
    '9': ['Nepali', 'English', 'Mathematics', 'Science', 'Social Studies', 'Health, Population & Environment', 'Occupation, Business & Technology', 'Optional I', 'Optional II'],
    '10': ['Nepali', 'English', 'Mathematics', 'Science', 'Social Studies', 'Health, Population & Environment', 'Occupation, Business & Technology', 'Optional I', 'Optional II'],
    '11': ['Nepali', 'English', 'Mathematics', 'Physics', 'Chemistry', 'Biology', 'Computer Science', 'Accountancy', 'Economics', 'Business Studies', 'Sociology', 'Political Science', 'Education', 'Mass Communication', 'Rural Development'],
    '12': ['Nepali', 'English', 'Mathematics', 'Physics', 'Chemistry', 'Biology', 'Computer Science', 'Accountancy', 'Economics', 'Business Studies', 'Sociology', 'Political Science', 'Education', 'Mass Communication', 'Rural Development'],
  };

  const getSubjectsForClass = (classLevel) => {
    return cdcSubjects[classLevel] || [];
  };

  useEffect(() => {
    fetchSubjects();
    fetchTeachers();
    fetchStats();
  }, []);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/subjects');
      if (response.data.success) {
        setSubjects(response.data.data || []);
      } else {
        // Handle case when backend returns success: false
        setSubjects([]);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      // Only show error message for actual errors, not for empty data
      if (error.response?.status !== 200) {
        enqueueSnackbar('Failed to fetch subjects', { variant: 'error' });
      }
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await axios.get('/api/teachers');
      if (response.data.success) {
        setTeachers(response.data.data.teachers || []);
      }
    } catch (error) {
      console.error('Failed to fetch teachers:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/subjects/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingSubject ? `/api/subjects/${editingSubject.subject_id}` : '/api/subjects';
      const method = editingSubject ? 'put' : 'post';
      
      const response = await axios[method](url, formData);
      
      if (response.data.success) {
        enqueueSnackbar(
          editingSubject ? 'Subject updated successfully' : 'Subject created successfully',
          { variant: 'success' }
        );
        setOpenDialog(false);
        resetForm();
        fetchSubjects();
        fetchStats();
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || 'Operation failed',
        { variant: 'error' }
      );
    }
  };

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({
      subject_name: subject.subject_name || '',
      subject_code: subject.subject_code || '',
      description: subject.description || '',
      type: subject.type || 'core',
      credits: subject.credits || '',
      teacher_id: subject.teacher_id || '',
      class_level: subject.class_level || '',
      is_active: subject.is_active !== undefined ? subject.is_active : true
    });
    setOpenDialog(true);
  };

  const handleDelete = async (subjectId) => {
    if (window.confirm('Are you sure you want to delete this subject?')) {
      try {
        const response = await axios.delete(`/api/subjects/${subjectId}`);
        if (response.data.success) {
          enqueueSnackbar('Subject deleted successfully', { variant: 'success' });
          fetchSubjects();
          fetchStats();
        }
      } catch (error) {
        enqueueSnackbar('Failed to delete subject', { variant: 'error' });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      subject_name: '',
      subject_code: '',
      description: '',
      type: 'core',
      credits: '',
      teacher_id: '',
      class_level: '',
      is_active: true
    });
    setEditingSubject(null);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'core': return 'primary';
      case 'elective': return 'secondary';
      case 'extra': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Subject Management
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.totalSubjects}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Total Subjects
                    </Typography>
                  </Box>
                  <BookIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.coreSubjects}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Core Subjects
                    </Typography>
                  </Box>
                  <SchoolIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.electiveSubjects}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Elective Subjects
                    </Typography>
                  </Box>
                  <AssignmentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.activeSubjects}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Active Subjects
                    </Typography>
                  </Box>
                  <CategoryIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchSubjects}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setOpenDialog(true);
            }}
          >
            Add Subject
          </Button>
        </Box>
      </Paper>

      {/* Subjects Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Subject Details</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Teacher</TableCell>
                <TableCell>Class Level</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">Loading...</TableCell>
                </TableRow>
              ) : subjects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">No subjects found</TableCell>
                </TableRow>
              ) : (
                subjects.map((subject) => (
                  <TableRow key={subject.subject_id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                          {subject.subject_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {subject.description || 'No description'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {subject.subject_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={subject.type?.charAt(0).toUpperCase() + subject.type?.slice(1) || 'Core'}
                        color={getTypeColor(subject.type)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {subject.teacher_name || 'Not Assigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{subject.class_level || 'All'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{subject.credits || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={subject.is_active ? 'Active' : 'Inactive'}
                        color={subject.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Edit Subject">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(subject)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Subject">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(subject.subject_id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSubject ? 'Edit Subject' : 'Add New Subject'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Class</InputLabel>
                <Select
                  value={formData.class_level}
                  label="Class"
                  onChange={(e) => setFormData({ ...formData, class_level: e.target.value, subject_name: '' })}
                >
                  {classLevels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {['Nursery', 'LKG', 'UKG'].includes(level) ? level : `Class ${level}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Subject Name</InputLabel>
                <Select
                  value={formData.subject_name}
                  label="Subject Name"
                  onChange={(e) => setFormData({ ...formData, subject_name: e.target.value })}
                  disabled={!formData.class_level}
                >
                  {getSubjectsForClass(formData.class_level).map((subject) => (
                    <MenuItem key={subject} value={subject}>{subject}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subject Code"
                value={formData.subject_code}
                onChange={(e) => setFormData({ ...formData, subject_code: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Subject Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Subject Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  {subjectTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Credits"
                type="number"
                value={formData.credits}
                onChange={(e) => setFormData({ ...formData, credits: e.target.value })}
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Assigned Teacher</InputLabel>
                <Select
                  value={formData.teacher_id}
                  label="Assigned Teacher"
                  onChange={(e) => setFormData({ ...formData, teacher_id: e.target.value })}
                >
                  <MenuItem value="">No Teacher Assigned</MenuItem>
                  {teachers.map((teacher) => (
                    <MenuItem key={teacher.teacher_id} value={teacher.teacher_id}>
                      {teacher.first_name} {teacher.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingSubject ? 'Update' : 'Create'} Subject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubjectManagement;
